'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { dequeueModalAtom } from '@/atoms/modalQueueAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalId } from '@heidi/schema/ml-scribe';
import { healthNzMigrationModalMessages, type HealthNzDismissal } from '@heidi/workspace';
import { healthNzMigrationPromptSuppressedAtom } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';

const HealthNzMigrationModalContent = dynamic(
  () =>
    import('./health-nz-migration-modal-content').then(
      (mod) => mod.HealthNzMigrationModalContent
    ),
  { ssr: false }
);

/**
 * Static notice: accounts on a Health NZ email move to the Health NZ team at
 * month end unless the user switches to a personal address. The flag is the
 * whole of the targeting.
 *
 * Title lives in the shell, not the lazy content, so the dialog has an
 * accessible name from the first frame.
 */
export function HealthNzMigrationModal() {
  const { isOpen, dismissible, handleClose } = useQueuedModal(
    ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL
  );
  // `onOpenChangeComplete` only fires on a *change*, so a shell mounting
  // already-open would never render its body.
  const [isDialogVisible, setIsDialogVisible] = useState(isOpen);
  const setSuppressed = useSetAtom(healthNzMigrationPromptSuppressedAtom);
  const dequeueModal = useSetAtom(dequeueModalAtom);
  const viewTrackedRef = useRef(false);

  // At most once — the queue can re-surface this after it yields, which would
  // otherwise inflate the funnel.
  useEffect(() => {
    if (isOpen && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      track({
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.HealthNzMigrationModal },
        options: { amplitude: false },
      });
    }
  }, [isOpen]);

  const handleDismiss = useCallback(
    (reason: HealthNzDismissal) => {
      if (reason === 'acknowledged') {
        // Records the dismissal in `seen_modals`, so it holds across devices.
        handleClose();
        return;
      }
      setSuppressed(true);
      // Drops a user-triggered entry (force-show / dev tools); the launch entry
      // leaves via the suppression atom above.
      dequeueModal(ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL);
    },
    [dequeueModal, handleClose, setSuppressed]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Only Esc, backdrop and the close X reach this path, so `modal_dismissed`
      // counts abandonment — never a CTA.
      if (open || !dismissible) return;
      track({
        event_name: TrackingEventName.ModalDismissed,
        props: {
          screen_name: ScreenName.HealthNzMigrationModal,
          auto_dismissed: false,
        },
        options: { amplitude: false },
      });
      handleDismiss('deferred');
    },
    [dismissible, handleDismiss]
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        className="max-w-[470px]"
        dismissible={dismissible}
        // No DialogDescription — the body is the description.
        aria-describedby={undefined}
        data-testid="health-nz-migration-modal"
      >
        {/* Clear the absolutely-positioned close icon. */}
        <DialogHeader className="pe-6">
          <DialogTitle>
            <FormattedMessage {...healthNzMigrationModalMessages.heading} />
          </DialogTitle>
        </DialogHeader>
        {/* Not gated on `isOpen`: the close animation outlives it. */}
        {isDialogVisible && <HealthNzMigrationModalContent onDismiss={handleDismiss} />}
      </DialogContent>
    </Dialog>
  );
}
